import { useMemo, useState } from 'react';
import { colormaps, defaultColormap } from './colormaps';

const ColorCodedWordsView = ({ 
  words, 
  pitch, 
  intensity, 
  segment, 
  broadcastTime
}) => {
  const [selectedColormap, setSelectedColormap] = useState(defaultColormap);
  const [useLocalMaxima, setUseLocalMaxima] = useState(true);
  const [showHelp, setShowHelp] = useState(false);

  // Helper function: Find local maxima
  const findLocalMaxima = (pitchSamples) => {
    const localMaxima = [];
    for (let i = 1; i < pitchSamples.length - 1; i++) {
      if (pitchSamples[i].pitch > pitchSamples[i-1].pitch && 
          pitchSamples[i].pitch > pitchSamples[i+1].pitch) {
        localMaxima.push({
          position: pitchSamples[i].position,
          pitch: pitchSamples[i].pitch
        });
      }
    }
    localMaxima.sort((a, b) => a.position - b.position);
    return localMaxima;
  };

  // Algorithm 1: Local maxima based positioning
  const calculateLetterPositionsByLocalMaxima = (pitchSamples, wordText) => {
    const letterPositions = [];
    const letters = wordText.split('');
    const localMaxima = findLocalMaxima(pitchSamples);

    // Determine shape based on number and position of maxima
    if (localMaxima.length === 0) {
      // No maxima - flat arrangement
      for (let i = 0; i < letters.length; i++) {
        letterPositions.push({
          letter: letters[i],
          verticalOffset: 0
        });
      }
    } else if (localMaxima.length === 1) {
      // Single peak - arch shape centered on the peak
      const peakPosition = localMaxima[0].position;
      for (let i = 0; i < letters.length; i++) {
        const letterProgress = i / Math.max(1, letters.length - 1);
        const distanceToPeak = Math.abs(letterProgress - peakPosition);
        const verticalOffset = Math.sin((1 - distanceToPeak) * Math.PI) * 10;
        letterPositions.push({
          letter: letters[i],
          verticalOffset: verticalOffset
        });
      }
    } else if (localMaxima.length === 2) {
      // Two peaks - M or W shape
      const firstPeak = localMaxima[0].position;
      const secondPeak = localMaxima[1].position;
      for (let i = 0; i < letters.length; i++) {
        const letterProgress = i / Math.max(1, letters.length - 1);
        let verticalOffset = 0;
        if (letterProgress < firstPeak) {
          verticalOffset = (letterProgress / firstPeak) * 8;
        } else if (letterProgress < secondPeak) {
          const progressBetween = (letterProgress - firstPeak) / (secondPeak - firstPeak);
          verticalOffset = 8 - (progressBetween * 16);
        } else {
          const progressAfter = (letterProgress - secondPeak) / (1 - secondPeak);
          verticalOffset = -8 + (progressAfter * 8);
        }
        letterPositions.push({
          letter: letters[i],
          verticalOffset: verticalOffset
        });
      }
    } else {
      // Multiple peaks - use polynomial fit for smooth curve
      const peakPositions = localMaxima.map(m => m.position);
      const averagePeakPosition = peakPositions.reduce((sum, pos) => sum + pos, 0) / peakPositions.length;
      
      for (let i = 0; i < letters.length; i++) {
        const letterProgress = i / Math.max(1, letters.length - 1);
        const distanceFromCenter = letterProgress - averagePeakPosition;
        const verticalOffset = -Math.pow(distanceFromCenter * 3, 2) + 8;
        letterPositions.push({
          letter: letters[i],
          verticalOffset: Math.max(-5, Math.min(8, verticalOffset))
        });
      }
    }

    return letterPositions;
  };

  // Algorithm 2: Original curve-based positioning
  const calculateLetterPositionsByCurve = (pitchSamples, wordText) => {
    const letterPositions = [];
    const letters = wordText.split('');
    const validPitchSamples = pitchSamples.filter(p => p.pitch > 0);

    if (validPitchSamples.length < 3) {
      // Fallback for words with insufficient pitch data
      for (let i = 0; i < letters.length; i++) {
        letterPositions.push({
          letter: letters[i],
          verticalOffset: 0
        });
      }
      return letterPositions;
    }

    // Calculate moving average for curve detection
    const smoothedForCurve = validPitchSamples.map((sample, i, arr) => {
      const windowSize = 2;
      const start = Math.max(0, i - windowSize);
      const end = Math.min(arr.length - 1, i + windowSize);
      const window = arr.slice(start, end + 1);
      const avg = window.reduce((sum, s) => sum + s.pitch, 0) / window.length;
      return { ...sample, smoothedPitch: avg };
    });

    // Detect curve type from smoothed data
    const startPitch = smoothedForCurve[0].smoothedPitch;
    const middlePitch = smoothedForCurve[Math.floor(smoothedForCurve.length / 2)].smoothedPitch;
    const endPitch = smoothedForCurve[smoothedForCurve.length - 1].smoothedPitch;
    
    const startToMiddle = middlePitch - startPitch;
    const middleToEnd = endPitch - middlePitch;
    
    let pitchCurve = 'flat';
    const curveThreshold = 15;
    
    if (startToMiddle > curveThreshold && middleToEnd < -curveThreshold) {
      pitchCurve = 'concave';
    } else if (startToMiddle < -curveThreshold && middleToEnd > curveThreshold) {
      pitchCurve = 'convex';
    } else if (endPitch > startPitch + curveThreshold * 0.7) {
      pitchCurve = 'rising';
    } else if (endPitch < startPitch - curveThreshold * 0.7) {
      pitchCurve = 'falling';
    }

    // Calculate letter positions based on pitch curve
    for (let i = 0; i < letters.length; i++) {
      const letterProgress = i / Math.max(1, letters.length - 1);
      const sampleIndex = Math.floor(letterProgress * (smoothedForCurve.length - 1));
      const sample = smoothedForCurve[sampleIndex];
      
      let verticalOffset = 0;
      
      switch (pitchCurve) {
        case 'concave':
          verticalOffset = Math.sin(letterProgress * Math.PI) * 8;
          break;
        case 'convex':
          verticalOffset = -Math.sin(letterProgress * Math.PI) * 8;
          break;
        case 'rising':
          verticalOffset = (letterProgress - 0.5) * 12;
          break;
        case 'falling':
          verticalOffset = (0.5 - letterProgress) * 12;
          break;
        default:
          if (sample) {
            const normalizedSamplePitch = (sample.smoothedPitch - 80) / (300 - 80);
            verticalOffset = (normalizedSamplePitch - 0.5) * 6;
          }
      }

      letterPositions.push({
        letter: letters[i],
        verticalOffset: verticalOffset
      });
    }

    return letterPositions;
  };

  // Smoothing function for pitch data
  const smoothPitchData = (pitchData, windowSize = 3) => {
    if (!pitchData?.xs || !pitchData?.ys) return pitchData;
    
    const smoothedYs = [];
    for (let i = 0; i < pitchData.ys.length; i++) {
      let sum = 0;
      let count = 0;
      
      for (let j = Math.max(0, i - windowSize); j <= Math.min(pitchData.ys.length - 1, i + windowSize); j++) {
        if (pitchData.ys[j] > 0) {
          sum += pitchData.ys[j];
          count++;
        }
      }
      
      smoothedYs.push(count > 0 ? sum / count : pitchData.ys[i]);
    }
    
    return { ...pitchData, ys: smoothedYs };
  };

  // Group words into responsive rows with constrained vertical positioning
  const groupWordsIntoRows = (coloredWords) => {
    if (!coloredWords || coloredWords.length === 0) return [];
    
    const rows = [];
    let currentRow = [];
    
    // Adjust words per row based on screen size
    const getWordsPerRow = () => {
      if (typeof window === 'undefined') return 4; // Default for SSR
      
      const width = window.innerWidth;
      if (width < 640) return 3;    // Mobile: 3 words per row
      if (width < 768) return 4;    // Small tablets: 4 words per row
      if (width < 1024) return 5;   // Tablets: 5 words per row
      if (width < 1280) return 6;   // Small desktop: 6 words per row
      return 7;                     // Large desktop: 7 words per row
    };
    
    const wordsPerRow = getWordsPerRow();
    
    // Simple sequential grouping
    for (let i = 0; i < coloredWords.length; i++) {
      currentRow.push({
        ...coloredWords[i],
        originalIndex: i
      });
      
      if (currentRow.length >= wordsPerRow || i === coloredWords.length - 1) {
        rows.push([...currentRow]);
        currentRow = [];
      }
    }
    
    return rows;
  };

  // Calculate constrained vertical positions within each row
  const calculateRowVerticalPositions = (wordRows) => {
    const MAX_VERTICAL_OFFSET = 25; // Maximum vertical movement within a row
    
    return wordRows.map(row => {
      // Find the pitch range within this row
      const pitches = row.map(word => word.maxPitch).filter(p => p > 0);
      if (pitches.length === 0) {
        return row.map(word => ({ ...word, constrainedVerticalPosition: 0 }));
      }
      
      const minPitch = Math.min(...pitches);
      const maxPitch = Math.max(...pitches);
      const pitchRange = maxPitch - minPitch;
      
      return row.map(word => {
        if (word.maxPitch <= 0) {
          return { ...word, constrainedVerticalPosition: 0 };
        }
        
        // Calculate normalized position within row's pitch range
        const normalizedPosition = pitchRange > 0 
          ? (word.maxPitch - minPitch) / pitchRange 
          : 0.5;
        
        // Convert to constrained vertical position
        const constrainedVerticalPosition = normalizedPosition * MAX_VERTICAL_OFFSET;
        
        return {
          ...word,
          constrainedVerticalPosition
        };
      });
    });
  };

  const coloredWords = useMemo(() => {
    if (!words || !words.length) {
      return [];
    }

    // Check the actual structure of pitch and intensity data
    const hasPitchData = pitch && pitch.xs && pitch.ys;
    const hasIntensityData = intensity && intensity.xs && intensity.ys;

    if (!hasPitchData || !hasIntensityData) {
      // Fallback: show words with neutral colors
      return words.map((word, index) => {
        return {
          word: word.text,
          textStyle: 'font-medium text-base',
          activeStyle: '',
          intensity: 0,
          pitch: 0,
          isActive: false,
          intensityLevel: 'no-data',
          pitchCurve: 'flat',
          backgroundGradient: 'linear-gradient(to right, #cccccc, #cccccc)',
          verticalPosition: 0,
          letterPositions: [],
          maxPitch: 0
        };
      });
    }

    // Smooth the pitch data
    const smoothedPitch = smoothPitchData(pitch, 5);

    // First pass: Calculate global pitch range across all words
    const wordMaxPitches = [];
    
    words.forEach(word => {
      const wordStart = word.start - segment.start;
      const wordEnd = word.end - segment.start;
      
      // Find pitch samples within this word's time range
      const wordPitchSamples = [];
      
      smoothedPitch.xs.forEach((time, index) => {
        if (time >= wordStart && time <= wordEnd && smoothedPitch.ys[index] > 0) {
          wordPitchSamples.push(smoothedPitch.ys[index]);
        }
      });
      
      // If no direct samples found, use nearest samples
      if (wordPitchSamples.length === 0) {
        const sampleCount = 5;
        for (let i = 0; i < sampleCount; i++) {
          const sampleTime = wordStart + (i / (sampleCount - 1)) * (wordEnd - wordStart);
          
          let closestPitch = 0;
          let minPitchDiff = Infinity;
          
          smoothedPitch.xs.forEach((time, pitchIndex) => {
            const diff = Math.abs(time - sampleTime);
            if (diff < minPitchDiff && smoothedPitch.ys[pitchIndex] > 0) {
              minPitchDiff = diff;
              closestPitch = smoothedPitch.ys[pitchIndex];
            }
          });
          
          if (closestPitch > 0) {
            wordPitchSamples.push(closestPitch);
          }
        }
      }
      
      const wordMaxPitch = wordPitchSamples.length > 0 ? Math.max(...wordPitchSamples) : 0;
      wordMaxPitches.push(wordMaxPitch);
    });

    // Calculate global range
    const validMaxPitches = wordMaxPitches.filter(p => p > 0);
    let globalMinPitch = validMaxPitches.length > 0 ? Math.min(...validMaxPitches) : 80;
    let globalMaxPitch = validMaxPitches.length > 0 ? Math.max(...validMaxPitches) : 300;
    
    // Use a smaller range for subtle positioning
    const pitchRange = globalMaxPitch - globalMinPitch;
    const targetRange = Math.max(30, Math.min(80, pitchRange));
    
    const centerPitch = (globalMinPitch + globalMaxPitch) / 2;
    globalMinPitch = centerPitch - targetRange / 2;
    globalMaxPitch = centerPitch + targetRange / 2;

    // broadcastTime is already relative to segment.start
    const currentTime = broadcastTime || 0;
    
    // Find the current active word
    const activeWordIndex = words.findIndex(word => {
      const wordStart = word.start - segment.start;
      const wordEnd = word.end - segment.start;
      return currentTime >= wordStart && currentTime <= wordEnd;
    });

    // Process each word
    return words.map((word, index) => {
      const wordStart = word.start - segment.start;
      const wordEnd = word.end - segment.start;
      const wordDuration = wordEnd - wordStart;

      // Get pitch and intensity samples
      const pitchSamples = [];
      const intensitySamples = [];
      const sampleCount = Math.max(15, word.text.length * 2);
      
      for (let i = 0; i < sampleCount; i++) {
        const sampleTime = wordStart + (i / (sampleCount - 1)) * wordDuration;
        
        // Find closest pitch value
        let closestPitch = 0;
        let minPitchDiff = Infinity;
        
        smoothedPitch.xs.forEach((time, pitchIndex) => {
          const diff = Math.abs(time - sampleTime);
          if (diff < minPitchDiff && smoothedPitch.ys[pitchIndex] > 0) {
            minPitchDiff = diff;
            closestPitch = smoothedPitch.ys[pitchIndex];
          }
        });
        
        pitchSamples.push({
          time: sampleTime,
          pitch: closestPitch,
          position: i / (sampleCount - 1)
        });

        // Find closest intensity value
        let closestIntensity = 0;
        let minIntensityDiff = Infinity;
        
        intensity.xs.forEach((time, intensityIndex) => {
          const diff = Math.abs(time - sampleTime);
          if (diff < minIntensityDiff) {
            minIntensityDiff = diff;
            closestIntensity = intensity.ys[intensityIndex];
          }
        });
        
        intensitySamples.push({
          time: sampleTime,
          intensity: closestIntensity,
          position: i / (sampleCount - 1)
        });
      }

      // Use smoothed max pitch for vertical positioning
      const wordMaxPitch = wordMaxPitches[index] || globalMinPitch;
      
      // Create intensity gradient
      const gradientStops = intensitySamples.map((sample, i) => {
        const position = (i / (intensitySamples.length - 1)) * 100;
        const color = colormaps[selectedColormap].getColor(sample.intensity);
        return `${color} ${position}%`;
      }).join(', ');
      
      const backgroundGradient = `linear-gradient(to right, ${gradientStops})`;

      // Calculate letter positions based on selected algorithm
      const letterPositions = useLocalMaxima 
        ? calculateLetterPositionsByLocalMaxima(pitchSamples, word.text)
        : calculateLetterPositionsByCurve(pitchSamples, word.text);

      // Calculate averages
      const averageIntensity = intensitySamples.reduce((sum, sample) => sum + sample.intensity, 0) / intensitySamples.length;
      const averagePitch = pitchSamples.reduce((sum, sample) => sum + sample.pitch, 0) / pitchSamples.length;

      // Determine pitch curve type for display
      let pitchCurve = 'flat';
      if (useLocalMaxima) {
        const localMaxima = findLocalMaxima(pitchSamples);
        if (localMaxima.length === 1) pitchCurve = 'single-peak';
        else if (localMaxima.length === 2) pitchCurve = 'double-peak';
        else if (localMaxima.length >= 3) pitchCurve = 'multi-peak';
      } else {
        // Use old curve detection for display
        const validPitchSamples = pitchSamples.filter(p => p.pitch > 0);
        if (validPitchSamples.length >= 3) {
          const startPitch = validPitchSamples[0].pitch;
          const middlePitch = validPitchSamples[Math.floor(validPitchSamples.length / 2)].pitch;
          const endPitch = validPitchSamples[validPitchSamples.length - 1].pitch;
          
          const startToMiddle = middlePitch - startPitch;
          const middleToEnd = endPitch - middlePitch;
          const curveThreshold = 15;
          
          if (startToMiddle > curveThreshold && middleToEnd < -curveThreshold) {
            pitchCurve = 'concave';
          } else if (startToMiddle < -curveThreshold && middleToEnd > curveThreshold) {
            pitchCurve = 'convex';
          } else if (endPitch > startPitch + curveThreshold * 0.7) {
            pitchCurve = 'rising';
          } else if (endPitch < startPitch - curveThreshold * 0.7) {
            pitchCurve = 'falling';
          }
        }
      }

      // Intensity level for text weight
      let textStyle = 'font-medium text-base';
      let intensityLevel = 'medium';
      
      if (averageIntensity > 70) {
        textStyle = 'font-bold text-lg';
        intensityLevel = 'high';
      } else if (averageIntensity > 50) {
        textStyle = 'font-semibold text-base';
        intensityLevel = 'medium';
      } else if (averageIntensity > 0) {
        intensityLevel = 'low';
      } else {
        intensityLevel = 'no-data';
      }

      const isActive = index === activeWordIndex;
      const activeStyle = isActive ? 'ring-2 ring-green-500 scale-105' : '';

      return {
        word: word.text,
        textStyle,
        activeStyle,
        intensity: averageIntensity,
        pitch: averagePitch,
        maxPitch: wordMaxPitch,
        isActive,
        intensityLevel,
        pitchCurve,
        backgroundGradient,
        letterPositions,
        algorithm: useLocalMaxima ? 'local-maxima' : 'curve-fitting'
      };
    });
  }, [words, pitch, intensity, segment, broadcastTime, selectedColormap, useLocalMaxima]);

  // Group words into responsive rows with constrained vertical positioning
  const wordRows = useMemo(() => {
    const groupedRows = groupWordsIntoRows(coloredWords);
    return calculateRowVerticalPositions(groupedRows);
  }, [coloredWords]);

  if (!words || !words.length) {
    return (
      <div className="bg-white p-4 rounded-lg border text-center text-gray-500">
        No word data available
      </div>
    );
  }

  const activeWord = coloredWords?.find(word => word.isActive);

  // Curve visualization components
  const ConcaveCurve = () => (
    <svg width="60" height="20" viewBox="0 0 60 20" className="text-gray-700">
      <path d="M5,15 Q30,5 55,15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
  );

  const ConvexCurve = () => (
    <svg width="60" height="20" viewBox="0 0 60 20" className="text-gray-700">
      <path d="M5,5 Q30,15 55,5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
  );

  const RisingCurve = () => (
    <svg width="60" height="20" viewBox="0 0 60 20" className="text-gray-700">
      <path d="M10,15 L50,5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
  );

  const FallingCurve = () => (
    <svg width="60" height="20" viewBox="0 0 60 20" className="text-gray-700">
      <path d="M10,5 L50,15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
  );

  return (
    <div className="bg-white p-4 rounded-lg border">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Speech Rhythm Visualization</h3>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Color Map:</label>
            <select 
              value={selectedColormap}
              onChange={(e) => setSelectedColormap(e.target.value)}
              className="text-sm border rounded px-2 py-1"
            >
              {Object.keys(colormaps).map(key => (
                <option key={key} value={key}>
                  {colormaps[key].name}
                </option>
              ))}
            </select>
          </div>

          {/* Help Button */}
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 transition-colors"
            title="Show help"
          >
            <span className="text-lg font-bold text-gray-600">?</span>
          </button>
        </div>
      </div>

      {/* Algorithm Toggle Button */}
      <div className="mb-4 flex justify-center">
        <button
          onClick={() => setUseLocalMaxima(!useLocalMaxima)}
          className="text-sm bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded px-3 py-1 transition-colors"
        >
          {useLocalMaxima ? 'Using Local Maxima' : 'Using Curve Fitting'}
        </button>
      </div>

      {/* Words display with responsive rows and constrained vertical positioning */}
      <div className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg min-h-40">
        {wordRows.map((row, rowIndex) => (
          <div 
            key={rowIndex}
            className="flex flex-wrap justify-center items-end gap-3 min-h-[70px] p-3 border-2 border-gray-300 rounded-lg bg-white/90 hover:bg-white transition-colors relative"
            style={{
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            {/* Row baseline */}
            <div className="absolute left-0 right-0 bottom-3 border-t border-dashed border-gray-400 opacity-50 pointer-events-none"></div>
            
            {row.map((item, wordIndex) => (
              <div 
                key={`${rowIndex}-${wordIndex}`}
                className="flex flex-col items-center transition-all duration-300 z-10"
                style={{ marginBottom: `${item.constrainedVerticalPosition}px` }}
              >
                <div
                  className={`px-3 py-2 rounded-lg border-2 transition-all duration-200 hover:scale-105 hover:shadow-md ${item.activeStyle}`}
                  style={{
                    background: item.backgroundGradient,
                    borderColor: '#4B5563',
                    minHeight: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '60px'
                  }}
                >
                  <div className="flex items-end space-x-1">
                    {item.letterPositions?.map((letterObj, letterIndex) => (
                      <span
                        key={letterIndex}
                        className={`inline-block transition-all duration-300 ${item.textStyle}`}
                        style={{
                          transform: `translateY(${letterObj.verticalOffset}px)`,
                          color: '#000000',
                          textShadow: '0 0 2px rgba(255,255,255,0.7)'
                        }}
                      >
                        {letterObj.letter}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">How to Read This Visualization</h3>
                <button
                  onClick={() => setShowHelp(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                <div className="text-sm">
                  <div className="font-medium mb-2">Background Color = Loudness Over Time</div>
                  <div className="h-6 rounded-lg border mb-1" style={{
                    background: `linear-gradient(to right, 
                      ${colormaps[selectedColormap].getColor(10)}, 
                      ${colormaps[selectedColormap].getColor(30)}, 
                      ${colormaps[selectedColormap].getColor(50)}, 
                      ${colormaps[selectedColormap].getColor(70)})`
                  }}></div>
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Quiet</span>
                    <span>Loud</span>
                  </div>
                </div>

                <div className="text-sm">
                  <div className="font-medium mb-2">Vertical Position = Highest Pitch</div>
                  <div className="flex items-end justify-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex flex-col items-center">
                      <div className="px-2 py-1 bg-red-100 border border-red-300 rounded text-xs">Low</div>
                      <div className="text-xs text-gray-600 mt-1">Low pitch</div>
                    </div>
                    <div className="flex flex-col items-center transform -translate-y-1">
                      <div className="px-2 py-1 bg-green-100 border border-green-300 rounded text-xs">Medium</div>
                      <div className="text-xs text-gray-600 mt-1">Medium pitch</div>
                    </div>
                    <div className="flex flex-col items-center transform -translate-y-3">
                      <div className="px-2 py-1 bg-blue-100 border border-blue-300 rounded text-xs">High</div>
                      <div className="text-xs text-gray-600 mt-1">High pitch</div>
                    </div>
                  </div>
                </div>

                <div className="text-sm">
                  <div className="font-medium mb-2">Letter Arrangement = Pitch Pattern</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-8 flex items-center justify-center">
                        <ConcaveCurve />
                      </div>
                      <div className="text-xs mt-1 text-center">Rising then falling</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-8 flex items-center justify-center">
                        <ConvexCurve />
                      </div>
                      <div className="text-xs mt-1 text-center">Falling then rising</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-8 flex items-center justify-center">
                        <RisingCurve />
                      </div>
                      <div className="text-xs mt-1 text-center">Consistently rising</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-8 flex items-center justify-center">
                        <FallingCurve />
                      </div>
                      <div className="text-xs mt-1 text-center">Consistently falling</div>
                    </div>
                  </div>
                </div>

                <div className="text-sm bg-blue-50 p-3 rounded-lg">
                  <div className="font-medium mb-1">Algorithm Toggle</div>
                  <p className="text-xs text-gray-600">
                    Switch between <strong>Local Maxima</strong> (detects pitch peaks) and <strong>Curve Fitting</strong> (fits smooth curves to pitch patterns) to see different interpretations of the pitch data.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowHelp(false)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorCodedWordsView;